// Input masks for the `form.draft` card (SCRUM-278).
//
// The clinic reads and types Brazilian formats; the runtime's confirm tools
// read digits-only documents/phones and ISO dates. `displayValue` goes one
// way, `payloadValue` the other, and the approval only ever sends the second.

import type { FormFieldType } from "@/components/generative-ui/envelope/types";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/** `000.000.000-00`, progressive while typing. */
export function maskCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)].filter(
    (part) => part.length > 0,
  );
  const tail = digits.slice(9, 11);
  const head = parts.join(".");
  return tail.length > 0 ? `${head}-${tail}` : head;
}

/** `(00) 00000-0000`, and `(00) 0000-0000` for a landline. */
export function maskPhone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  const area = digits.slice(0, 2);
  const rest = digits.slice(2);
  const split = digits.length > 10 ? 5 : 4;
  if (rest.length <= split) return `(${area}) ${rest}`;
  return `(${area}) ${rest.slice(0, split)}-${rest.slice(split)}`;
}

/** `dd/mm/aaaa`, progressive while typing. */
export function maskDate(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(
    (part) => part.length > 0,
  );
  return parts.join("/");
}

/** `hh:mm`, progressive while typing; a short hour is not padded (as `maskDate`). */
export function maskTime(value: string): string {
  const digits = onlyDigits(value).slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
}

/** `1990-04-21` → `21/04/1990`. Anything else is masked as typed. */
export function isoToBrDate(value: string): string {
  const match = ISO_DATE.exec(value);
  if (!match) return maskDate(value);
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** `21/04/1990` → `1990-04-21`, or null when the date does not exist. */
export function brDateToIso(value: string): string | null {
  const digits = onlyDigits(value);
  if (digits.length !== 8) return null;
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (month < 1 || month > 12 || day < 1 || year < 1900) return null;
  const iso = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.getUTCDate() !== day) return null;
  return iso;
}

/** What the clinic sees, from what the runtime sent. */
export function displayValue(type: FormFieldType, value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (raw.length === 0) return "";
  switch (type) {
    case "cpf":
      return maskCpf(raw);
    case "phone":
      return maskPhone(raw);
    case "date":
      return isoToBrDate(raw);
    default:
      return raw;
  }
}

/** What the approval sends, from what the clinic typed. */
export function payloadValue(type: FormFieldType, value: string): string | null {
  const raw = value.trim();
  if (raw.length === 0) return null;
  switch (type) {
    case "cpf":
    case "phone":
      return onlyDigits(raw);
    case "date":
      return brDateToIso(raw) ?? raw;
    default:
      return raw;
  }
}

/** Re-applies the field's mask to a keystroke. */
export function maskInput(type: FormFieldType, value: string): string {
  switch (type) {
    case "cpf":
      return maskCpf(value);
    case "phone":
      return maskPhone(value);
    case "date":
      return maskDate(value);
    case "time":
      return maskTime(value);
    default:
      return value;
  }
}
